import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { useAudio } from '../contexts/AudioContext';
import '../styles/Home.css';

const Home = () => {
  const navigate = useNavigate();
  const { startGame } = useGame();
  const { playMusic, playSound, isMusicPlaying, toggleMusic, isMuted, toggleMute, isAudioInitialized, audioReady } = useAudio();
  const [showCompetitiveModal, setShowCompetitiveModal] = useState(false);
  const [playerName, setPlayerName] = useState('');

  // Efecto para iniciar la música cuando se carga la página
  useEffect(() => {
    console.log('Home montado, configurando audio...');
    
    // Verificar si venimos de "Terminar Juego"
    const musicStopped = localStorage.getItem('mielMaster_musicStopped') === 'true';
    
    // Si venimos de "Terminar Juego", no reproducir música automáticamente
    // y limpiar la bandera para futuras visitas a la página principal
    if (musicStopped) {
      console.log('Música detenida por "Terminar Juego", no reproducir automáticamente');
      localStorage.removeItem('mielMaster_musicStopped');
      return;
    }
    
    // Función simple para manejar la interacción del usuario
    const handleUserInteraction = () => {
      if (!isMusicPlaying) {
        console.log('Interacción del usuario detectada, intentando reproducir música...');
        playMusic();
      }
    };
    
    // Añadir event listeners para detectar interacción del usuario
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
    
    // Intentar reproducir la música después de un breve retraso
    const initialPlayTimer = setTimeout(() => {
      console.log('Intentando reproducción inicial de música...');
      playMusic();
    }, 500);
    
    // Limpiar al desmontar
    return () => {
      clearTimeout(initialPlayTimer);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, [playMusic, isMusicPlaying]);

  const handleManualAudioStart = () => {
    console.log('Activación manual de audio solicitada');
    playMusic();
    playSound('button_click');
  };

  const handleStartGame = (competitive = false) => {
    // Intentar reproducir el sonido del botón
    try {
      playSound('button_click');
    } catch (error) {
      console.warn('No se pudo reproducir el sonido del botón:', error);
    }
    
    if (competitive) {
      setShowCompetitiveModal(true);
    } else {
      startGame('', false);
      navigate('/game');
    }
  };

  const handleCompetitiveSubmit = (e) => {
    e.preventDefault();
    if (playerName.trim()) {
      try {
        playSound('game_start');
      } catch (error) {
        console.warn('No se pudo reproducir el sonido de inicio:', error);
      }
      startGame(playerName, true);
      navigate('/game');
    }
  };

  return (
    <div className="home-container">
      {/* Imagen 01.png en la parte superior izquierda */}
      <div className="top-left-image">
        <img src={require('../assets/images/01.png')} alt="Logo" />
      </div>
      
      {/* Imagen Amolamiel.svg en la parte superior derecha */}
      <div className="top-right-image">
        <img src="/Amolamiel.svg" alt="Amolamiel" />
      </div>
      
      {/* Imagen expo-2025.png en la parte inferior izquierda */}
      <div className="bottom-left-image">
        <img src={require('../assets/images/expo-2025.png')} alt="Expo 2025" />
      </div>
      
      <div className="background-animation">
        <div className="meadow"></div>
        <div className="trees"></div>
        <div className="bees-container">
          {[...Array(Math.floor(8 + Math.random() * 8))].map((_, index) => (
            <div 
              key={index} 
              className="bee" 
              style={{
                animationDelay: `${index * 0.3}s`,
                animationDuration: `${12 + Math.random() * 8}s`,
                top: `${10 + Math.random() * 80}%`,
                left: `${Math.random() * 100}%`,
                transform: `scale(${0.7 + Math.random() * 0.6})`
              }}
            ></div>
          ))}
        </div>
      </div>

      <div className="content">
        <h1 className="title floating">Master Miel</h1>
        <h2 className="subtitle">Adiviná el sabor</h2>

        <div className="buttons-container">
          <button 
            className="btn btn-primary start-btn"
            onClick={() => handleStartGame(false)}
          >
            Comenzar Juego
          </button>
          
          <button 
            className="btn btn-secondary"
            onClick={() => {
              playSound('button_click');
              navigate('/instructions');
            }}
          >
            Instrucciones
          </button>
          
          <button 
            className="btn btn-secondary"
            onClick={() => {
              playSound('button_click');
              navigate('/honey-info');
            }}
          >
            Información sobre mieles
          </button>

          <button 
            className="btn btn-competitive"
            onClick={() => handleStartGame(true)}
          >
            Modo Competitivo
          </button>
          
          {!isMusicPlaying && (
            <button 
              className="btn btn-audio-fix"
              onClick={handleManualAudioStart}
            >
              Activar Juego
            </button>
          )}
        </div>
        
      </div>

      {showCompetitiveModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Modo Competitivo</h2>
            <p>Ingresa tu nombre para comenzar:</p>
            <form onSubmit={handleCompetitiveSubmit}>
              <input 
                type="text" 
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Tu nombre"
                maxLength={20}
                required
              />
              <div className="modal-buttons">
                <button type="submit" className="btn btn-primary">Comenzar</button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    playSound('button_click');
                    setShowCompetitiveModal(false);
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
